import {
  Alert,
  Badge,
  Button,
  Checkbox,
  FileInput,
  Flex,
  Group,
  List,
  Modal,
  Progress,
  ScrollArea,
  Select,
  Table,
  Text,
} from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useCompanyUsers } from 'src/secret-menu/conversation-debugger/hooks/useCompanyUsers';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import {
  BULK_UPLOAD_FORMAT_CONFIG,
  BulkUploadFormat,
  BulkUploadResult,
  useBulkClientUpload,
} from './useBulkClientUpload';

const FORMAT_OPTIONS = Object.entries(BULK_UPLOAD_FORMAT_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}));

const PREVIEW_COLUMNS = [
  'Forename',
  'Surname',
  'Title',
  'Client Adviser Name',
  'Preferred Email',
  'Date of Birth',
  'National Insurance Number',
] as const;

interface PreviewRow {
  values: Record<string, string>;
  isEntityRow: boolean;
}

interface CsvPreview {
  rows: PreviewRow[];
  importCount: number;
  skipCount: number;
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === delimiter && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

function detectDelimiter(headerLine: string): ',' | ';' {
  const commas = (headerLine.match(/,/g) ?? []).length;
  const semicolons = (headerLine.match(/;/g) ?? []).length;
  return commas > semicolons ? ',' : ';';
}

async function parseFourPointPreview(file: File): Promise<CsvPreview> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  // Line 0: "Afternoon Field Mapping" — skip
  // Line 1: "FourPoint Data" — actual column headers
  if (lines.length < 3) return { rows: [], importCount: 0, skipCount: 0 };

  const delimiter = detectDelimiter(lines[1]);
  const headers = parseCsvLine(lines[1], delimiter);
  const colIndex = (name: string) => headers.indexOf(name);

  const rows: PreviewRow[] = [];
  let importCount = 0;
  let skipCount = 0;

  for (const line of lines.slice(2)) {
    const cells = parseCsvLine(line, delimiter);
    if (cells.every(c => !c.trim())) continue;

    const forename = cells[colIndex('Forename')]?.trim() ?? '';
    const isEntityRow = forename === '.';

    const values: Record<string, string> = {};
    for (const col of PREVIEW_COLUMNS) {
      const idx = colIndex(col);
      values[col] = idx >= 0 ? (cells[idx]?.trim() ?? '') : '';
    }

    rows.push({ values, isEntityRow });
    if (isEntityRow) skipCount++;
    else importCount++;
  }

  return { rows, importCount, skipCount };
}

type Props = {
  companyId: string;
  opened: boolean;
  onClose: () => void;
};

export default function BulkUploadModal({ companyId, opened, onClose }: Props) {
  const [format, setFormat] = useState<BulkUploadFormat>('ebs');
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [defaultAdviserName, setDefaultAdviserName] = useState<string | null>(null);
  const { uploadClients, isPending, result, errorMessage, progress, reset } =
    useBulkClientUpload(companyId);

  const handleFormatChange = (newFormat: BulkUploadFormat) => {
    const newConfig = BULK_UPLOAD_FORMAT_CONFIG[newFormat];
    if (newConfig.fileExtension !== BULK_UPLOAD_FORMAT_CONFIG[format].fileExtension) {
      setFile(null);
    }
    if (!newConfig.supportsDryRun) {
      setDryRun(false);
    }
    if (!newConfig.requiresDefaultAdviser) {
      setDefaultAdviserName(null);
    }
    setFormat(newFormat);
  };

  const handleClose = () => {
    setFile(null);
    setDryRun(false);
    setDefaultAdviserName(null);
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      await uploadClients({
        format,
        file,
        dryRun,
        defaultAdviserName: defaultAdviserName ?? undefined,
      });
      setFile(null);
    } catch (e) {
      notifyAndLogError('Bulk client upload failed', 'BulkUploadModal', e);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Bulk upload clients" centered size="xl">
      <ModalContent
        companyId={companyId}
        errorMessage={errorMessage}
        result={result}
        format={format}
        setFormat={handleFormatChange}
        file={file}
        setFile={setFile}
        dryRun={dryRun}
        setDryRun={setDryRun}
        defaultAdviserName={defaultAdviserName}
        setDefaultAdviserName={setDefaultAdviserName}
        progress={progress}
        isPending={isPending}
        handleClose={handleClose}
        handleUpload={handleUpload}
      />
    </Modal>
  );
}

type ModalContentProps = {
  companyId: string;
  errorMessage: string | null;
  result: BulkUploadResult | null;
  format: BulkUploadFormat;
  setFormat: (format: BulkUploadFormat) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  dryRun: boolean;
  setDryRun: (dryRun: boolean) => void;
  defaultAdviserName: string | null;
  setDefaultAdviserName: (adviserName: string | null) => void;
  progress: number | null;
  isPending: boolean;
  handleClose: () => void;
  handleUpload: () => void;
};

function ModalContent({
  companyId,
  errorMessage,
  result,
  format,
  setFormat,
  file,
  setFile,
  dryRun,
  setDryRun,
  defaultAdviserName,
  setDefaultAdviserName,
  progress,
  isPending,
  handleClose,
  handleUpload,
}: ModalContentProps) {
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const { users } = useCompanyUsers(companyId);
  const formatConfig = BULK_UPLOAD_FORMAT_CONFIG[format];
  const adviserOptions = (users ?? []).map(user => `${user.firstName} ${user.lastName}`);
  const missingAdviser = formatConfig.requiresDefaultAdviser && !defaultAdviserName;

  useEffect(() => {
    if (file && format === 'four_point_wealth') {
      parseFourPointPreview(file).then(setPreview);
    } else {
      setPreview(null);
    }
  }, [file, format]);

  if (errorMessage) {
    return (
      <Flex direction="column" gap="md">
        <Alert color="red" title="Upload failed">
          {errorMessage}
        </Alert>
        <Group justify="flex-end">
          <Button onClick={handleClose}>Done</Button>
        </Group>
      </Flex>
    );
  }

  if (result) {
    return (
      <Flex direction="column" gap="md">
        <Text>
          {result.dryRun ? 'Dry run complete. Would create' : 'Created'}{' '}
          <strong>{result.clientsCreated}</strong> clients across{' '}
          <strong>{result.familiesCreated}</strong> families.
        </Text>
        {result.reportLocation && (
          <Text size="sm" c="dimmed">
            Outcome report: {result.reportLocation}
          </Text>
        )}
        {result.skipped.length > 0 && (
          <>
            <Text c="dimmed" size="sm">
              {result.skipped.length} row(s) skipped:
            </Text>
            <List size="sm" c="dimmed">
              {result.skipped.map((reason, i) => (
                <List.Item key={i}>{reason}</List.Item>
              ))}
            </List>
          </>
        )}
        <Group justify="flex-end">
          <Button onClick={handleClose}>Done</Button>
        </Group>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Format"
        data={FORMAT_OPTIONS}
        value={format}
        onChange={v => v && setFormat(v as BulkUploadFormat)}
      />
      {formatConfig.requiresDefaultAdviser && (
        <Select
          label="Adviser"
          description="This file has no adviser, so every client is assigned to the adviser you choose"
          placeholder="Choose an adviser"
          data={adviserOptions}
          value={defaultAdviserName}
          onChange={setDefaultAdviserName}
          searchable
        />
      )}
      <FileInput
        label="Spreadsheet"
        accept={formatConfig.fileExtension}
        value={file}
        onChange={setFile}
        leftSection={<IconUpload size={16} />}
        placeholder={`Choose ${formatConfig.fileExtension.slice(1).toUpperCase()} file`}
      />
      {formatConfig.supportsDryRun && (
        <Checkbox
          label="Dry run"
          description="Runs the full upload without creating anything and produces the outcome report"
          checked={dryRun}
          onChange={event => setDryRun(event.currentTarget.checked)}
        />
      )}
      {preview && <FourPointPreview preview={preview} />}
      {progress != null && <UploadProgress value={progress} />}
      <Group justify="flex-end" mt="sm">
        <Button variant="subtle" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleUpload} disabled={!file || missingAdviser} loading={isPending}>
          Upload
        </Button>
      </Group>
    </Flex>
  );
}

type PreviewFilter = 'all' | 'import' | 'skip';

function FourPointPreview({ preview }: { preview: CsvPreview }) {
  const [filter, setFilter] = useState<PreviewFilter>('all');

  const toggle = (value: 'import' | 'skip') =>
    setFilter(current => (current === value ? 'all' : value));

  const visibleRows = preview.rows.filter(row => {
    if (filter === 'import') return !row.isEntityRow;
    if (filter === 'skip') return row.isEntityRow;
    return true;
  });

  return (
    <Flex direction="column" gap="xs">
      <Group gap="xs">
        <Badge
          color="green"
          variant={filter === 'import' ? 'filled' : 'light'}
          style={{ cursor: 'pointer' }}
          onClick={() => toggle('import')}
        >
          {preview.importCount} to import
        </Badge>
        {preview.skipCount > 0 && (
          <Badge
            color="gray"
            variant={filter === 'skip' ? 'filled' : 'light'}
            style={{ cursor: 'pointer' }}
            onClick={() => toggle('skip')}
          >
            {preview.skipCount} entity rows skipped
          </Badge>
        )}
      </Group>
      <ScrollArea h={300} type="auto">
        <Table striped withTableBorder withColumnBorders fz="xs" style={{ whiteSpace: 'nowrap' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              {PREVIEW_COLUMNS.map(col => (
                <Table.Th key={col}>{col}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleRows.map((row, i) => (
              <Table.Tr key={i} c={row.isEntityRow ? 'dimmed' : undefined}>
                <Table.Td>
                  {row.isEntityRow ? (
                    <Badge size="xs" color="gray" variant="outline">
                      skip
                    </Badge>
                  ) : (
                    <Badge size="xs" color="green" variant="outline">
                      import
                    </Badge>
                  )}
                </Table.Td>
                {PREVIEW_COLUMNS.map(col => (
                  <Table.Td key={col}>{row.values[col]}</Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Flex>
  );
}

function UploadProgress({ value }: { value: number }) {
  return (
    <Group gap="xs" mt="sm" align="center">
      <Progress value={value} style={{ flex: 1 }} />
      <Text size="sm" c="dimmed">
        {value}%
      </Text>
    </Group>
  );
}
